/**
 * SA-1034 PR 5 (spec §3.1): the one tooltip helper. It is the basis
 * `Tooltip` / `TooltipTrigger` and the app's `TooltipContent` below — the
 * basis file is hash-pinned and untouched. `asChild` means the child must
 * take a ref and spread props (a basis Button, a plain button / span / div,
 * a NavLink, a Radix Close or DropdownMenuTrigger all do). A DISABLED button
 * fires no pointer events: wrap it in a focusable `span` first, the SA-1077
 * shape on the band's Waiting button. The delay is 300ms: the basis
 * provider's 0 makes a bar of icon buttons flicker as the pointer crosses
 * it, and Radix's own 700 reads as broken. A converted control keeps its
 * `aria-label` and loses its `title` (two tooltips would show).
 */
import type { ComponentProps, ReactElement } from 'react'
import { Tooltip, TooltipContent as BasisTooltipContent, TooltipTrigger } from './basis/tooltip'
import { cn } from '@/utils'

export const TIP_DELAY_MS = 300
export type TipSide = 'top' | 'bottom' | 'left' | 'right'

/**
 * SA-1123 (spec §3): every tooltip is inverted — a near-black label in light,
 * a near-white one in dark — and never the brand color. The basis content is
 * `bg-primary text-primary-foreground`; `cn` is tailwind-merge, so this class
 * replaces both, and a caller's own class still comes last and wins. The arrow
 * is drawn inside the basis file, so styles/globals.css repaints it.
 */
export const TIP_CONTENT_CLASS = 'bg-foreground text-background'

/** The basis `TooltipContent` with the app's recipe. `@/components/ui` exports this one, never the basis one. */
export function TooltipContent({ className, ...props }: ComponentProps<typeof BasisTooltipContent>) {
  return <BasisTooltipContent className={cn(TIP_CONTENT_CLASS, className)} {...props} />
}

/** `open` / `onOpenChange` hold the tip shut where a caller must (SA-1418 B1: the user menu's
 *  returned focus); left out, the tip is uncontrolled. `contentClassName` goes on the content after the recipe
 *  (SA-1417: a width cap, so a long tip wraps). */
export function Tip({ text, side = 'top', open, onOpenChange, contentClassName, children }: {
  text: string; side?: TipSide; open?: boolean; onOpenChange?: (open: boolean) => void; contentClassName?: string
  children: ReactElement
}) {
  return (
    <Tooltip delayDuration={TIP_DELAY_MS} open={open} onOpenChange={onOpenChange}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className={contentClassName}>{text}</TooltipContent>
    </Tooltip>
  )
}
